import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Leaf, ArrowLeft, Mail, Lock, Eye, EyeOff } from "lucide-react";

const CoachLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/coach/login`, {
        email,
        password
      });
      
      const { access_token, coach } = response.data;
      localStorage.setItem("coach_token", access_token);
      
      toast.success(`Welcome back, ${coach.first_name}!`);
      navigate("/coach-dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[#7C9A92]" />
            <span className="font-serif text-xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#78716C] hover:text-[#2C332B] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-stone-100 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-[#7C9A92]" />
              </div>
              <CardTitle className="font-serif text-3xl font-light text-[#2C332B]">
                Coach Login
              </CardTitle>
              <p className="text-[#78716C] mt-2">
                Access your coach dashboard
              </p>
            </CardHeader>
            
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                    <Input
                      type="email"
                      placeholder="coach@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 pl-12 rounded-xl border-stone-200 bg-stone-50/50"
                      data-testid="coach-login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 pl-12 pr-12 rounded-xl border-stone-200 bg-stone-50/50"
                      data-testid="coach-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#2C332B]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <Link 
                    to="/forgot-password?type=coach" 
                    className="text-sm text-[#7C9A92] hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl bg-[#7C9A92] hover:bg-[#6B8A82] text-white text-lg"
                  data-testid="coach-login-btn"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-stone-100 text-center">
                <p className="text-[#78716C] mb-4">
                  Not a coach yet?
                </p>
                <Link to="/apply-to-coach">
                  <Button variant="outline" className="rounded-full px-6">
                    Apply to Coach
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default CoachLoginPage;
